<template>
  <div
    id="globe-container"
    ref="containerRef"
    :class="{ 'overlay-mode': isOverlayMode, 'experience-mode': flowsActive }"
  >
    <canvas id="globe-canvas" ref="canvasRef"></canvas>

    <div
      class="globe-interaction-area"
      @mousedown="globe.onMouseDown"
      @mousemove="globe.onMouseMove"
      @mouseup="globe.onMouseUp"
      @mouseleave="globe.onMouseLeave"
      @touchstart="globe.onTouchStart"
      @touchmove.prevent="globe.onTouchMove"
      @touchend="globe.onTouchEnd"
    ></div>

    <div class="experience-stats hybrid-stats" :class="{ visible: flowsActive }">
      <div class="stat-item fade-stat visible">
        <div class="stat-number">$3.9B</div>
        <div class="stat-label">of institutional commitments</div>
      </div>
      <div class="stat-item fade-stat visible">
        <div class="stat-number">80%</div>
        <div class="stat-label">for novel strategies that hadn't<br>previously received institutional backing</div>
      </div>
      <div class="stat-item fade-stat visible">
        <div class="stat-number">75%</div>
        <div class="stat-label">for strategies that exhibit low correlation<br>to traditional asset classes</div>
      </div>
      <div class="stat-item fade-stat visible">
        <div class="stat-number">&gt;500</div>
        <div class="stat-label">investors worldwide who engaged<br>in diligence on multiple clients</div>
      </div>
    </div>

    <div
      class="typewriter-text globe-experience-text"
      role="button"
      tabindex="0"
      @click="toggleFlows"
    >
      <span class="typewriter-content">{{ flowsActive ? '[close visualization]' : '[visualize our experience]' }}</span>
    </div>

    <div
      v-if="isOverlayMode"
      class="typewriter-text globe-close-text"
      role="button"
      tabindex="0"
      @click="closeGlobe"
    >
      <span class="typewriter-content">[learn more]</span>
    </div>

    <!-- <div class="globe-logo" :class="{ moved: flowsActive }">&capital</div> -->


    <div
      class="manager-card-stack"
      :class="{ visible: flowsActive && selectedManager }"
      @touchstart="handleSwipeStart"
      @touchend="handleSwipeEnd"
      @mousedown="handleSwipeStart"
      @mouseup="handleSwipeEnd"
    >
      <div
        v-for="(manager, index) in managerCards"
        :key="manager.city"
        class="manager-card"
        :class="getStackClass(index)"
        @click="selectManager(manager.city)"
      >
        <div class="card-close" @click.stop="closeManagerCard"></div>
        <h3 class="card-city-name">{{ manager.city }}</h3>
        <p class="card-description">{{ manager.description }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAppStore } from '@/stores/app';
import { useDevice } from '@/composables/useDevice';
import { useParticleGlobe } from '@/composables/useParticleGlobe';

const store = useAppStore();
const { getGlobeConfig } = useDevice();

// Refs
const canvasRef = ref(null);
const containerRef = ref(null);
const globeConfig = ref(getGlobeConfig());

const isOverlayMode = computed(() => store.globeActive);
const flowsActive = computed(() => store.experienceSectionActive && store.flowsVisible);

// FIX: Layers computed property - reads directly from Pinia store
const layers = computed(() => [
  { id: 'managers', label: 'Manager Locations', color: 'var(--fg)', enabled: store.layers.managers },
  { id: 'flows', label: 'Capital Flows', color: 'var(--fg)', enabled: store.layers.flows },
  { id: 'investors', label: 'Engaged Investors', color: '#3B82F6', enabled: store.layers.investors }
]);

// Manager Data
const managerCards = ref([
  { city: 'London', description: 'Scaling multiple novel strategies across multiple firms...' },
  { city: 'Chicago', description: 'Multiple novel strategies in the litigation funding space...' },
  { city: 'Frankfurt', description: 'Complex transition to a Fund IV for a regional PE strategy' },
  { city: 'Paris', description: 'Established firm providing private credit / specialty finance' },
  { city: 'Madrid', description: 'Fund II for a dedicated single-country private equity strategy' },
  { city: 'San Francisco', description: 'New strategy in the private credit space' }
]);

// Selection State
const selectedManager = ref(null);
const selectedIndex = computed(() =>
  managerCards.value.findIndex(m => m.city === selectedManager.value)
);

// Initialize Globe
const globe = useParticleGlobe(canvasRef, globeConfig.value);

// --- Card Stack Logic ---

function selectManager(city) {
  selectedManager.value = city;
  globe.selectedManager.value = city; // Sync with Three.js
}

function closeManagerCard() {
  selectedManager.value = null;
  globe.selectedManager.value = null;
}

// Calculate stack classes (stack-1, stack-2, etc.) based on current selection
function getStackClass(index) {
  if (!selectedManager.value) return '';
  if (index === selectedIndex.value) return 'active';

  const total = managerCards.value.length;
  const current = selectedIndex.value;

  // Calculate relative position wrapping around the array
  let relative = index - current;
  if (relative < 0) relative += total;

  if (relative === 1) return 'stack-1';
  if (relative === 2) return 'stack-2';
  if (relative === 3) return 'stack-3';

  return 'hidden';
}

// --- Swipe Logic for Cards ---
let swipeStartX = 0;
let swipeStartY = 0;

function handleSwipeStart(e) {
  swipeStartX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
  swipeStartY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
}

function handleSwipeEnd(e) {
  const endX = e.type.includes('touch') ? e.changedTouches[0].clientX : e.clientX;
  const endY = e.type.includes('touch') ? e.changedTouches[0].clientY : e.clientY;
  const deltaX = endX - swipeStartX;
  const deltaY = endY - swipeStartY;

  // If horizontal swipe > 50px
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
    navigateStack(deltaX > 0 ? -1 : 1); // Right = prev, Left = next
  }
}

function navigateStack(direction) {
  if (selectedIndex.value === -1) return;
  let newIndex = selectedIndex.value + direction;
  if (newIndex < 0) newIndex = managerCards.value.length - 1;
  if (newIndex >= managerCards.value.length) newIndex = 0;

  selectManager(managerCards.value[newIndex].city);
}

// --- Globe Controls ---

function toggleFlows() {
  store.toggleFlows();
  globe.toggleFlows(store.flowsVisible);
}

// FIX: Simplified toggleLayer - just calls Pinia action
function toggleLayer(layerId) {
  // Use Pinia action to toggle the layer state
  store.toggleLayer(layerId);
  // No need for local state or manual synchronization anymore
}

function closeGlobe() {
  store.closeGlobe();
}

// --- Lifecycle ---

onMounted(() => {
  if (containerRef.value) {
    globe.init(containerRef.value);
    window.addEventListener('resize', globe.onWindowResize);

    // Watch for DOM elements to attach IntersectionObserver
    const expSection = document.getElementById('globe-window');
    if (expSection) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
             // Auto-activate flows if entering section
             if (!store.flowsVisible) toggleFlows();
             store.setExperienceSectionActive(true);
          } else if (!entry.isIntersecting) {
             store.setExperienceSectionActive(false);
          }
        });
      }, { threshold: 0.3 });
      observer.observe(expSection);
    }
  }
});

// Watch for flows active state to trigger particle morph
watch(flowsActive, (val) => {
  if (globe && globe.setExperienceActive) {
    globe.setExperienceActive(val);
  }
});

// Watch for Let's Talk active state to trigger booth morph
watch(() => store.letsTalkActive, (val) => {
  if (globe && globe.setLetsTalkActive) {
    globe.setLetsTalkActive(val);
  }
});

onUnmounted(() => {
  globe.cleanup();
  window.removeEventListener('resize', globe.onWindowResize);
});
</script>

<style scoped>
/* Critical fixes for interaction */
#globe-canvas {
  pointer-events: none;
  z-index: 1;
}
.globe-interaction-area {
  position: absolute;
  inset: 0;
  z-index: 2; /* Below UI, above canvas */
  cursor: grab;
  pointer-events: auto;
}
.manager-card-stack {
  pointer-events: none; /* Let clicks pass through container */
}
.manager-card {
  pointer-events: auto; /* Re-enable clicks on cards */
}
</style>
